import { isRestrictedArtwork } from "@/utils/artworkVisibility";

export const SOLD_STATUS_KEYWORDS = ["sold"];

export function hasTransactionHistory(artwork, getArtworkTransactionSource) {
  const source = getArtworkTransactionSource(artwork);
  if (Array.isArray(source)) {
    return source.length > 0;
  }
  return Boolean(source && typeof source === "object" && Object.keys(source).length > 0);
}

export function isLikelySoldArtwork(artwork) {
  if (!artwork) {
    return false;
  }

  const status = artwork.artwork_status;
  if (typeof status === "string") {
    const normalizedStatus = status.toLowerCase();
    if (SOLD_STATUS_KEYWORDS.some((keyword) => normalizedStatus.includes(keyword))) {
      return true;
    }
  }

  const metadata = artwork.product_metadata ?? {};
  if (metadata.has_been_sold === true) {
    return true;
  }

  if (typeof metadata.artwork_bought_at === "number") {
    return true;
  }

  return false;
}

export function resolveArtworkSlug(artwork, fallback) {
  if (!artwork) {
    return fallback ?? null;
  }

  return (
    artwork.slug
    ?? artwork.product_slug
    ?? artwork.artwork_slug
    ?? artwork.artwork?.slug
    ?? artwork.product?.slug
    ?? artwork.artwork_data?.slug
    ?? artwork.product_data?.slug
    ?? fallback
    ?? null
  );
}

export function resolveArtworkHoldingIdentifiers(artwork) {
  if (!artwork || typeof artwork !== "object") {
    return {
      holdingId: null,
      ownerId: null,
      holdingType: null,
    };
  }

  const holdingId = (
    artwork?.holding_id
    ?? artwork?.product_id
    ?? artwork?.productId
    ?? artwork?.metadata?.holding_id
    ?? artwork?.product_metadata?.holding_id
    ?? artwork?.metadata?.limited_edition?.copy_id
    ?? artwork?.product_metadata?.limited_edition?.copy_id
    ?? null
  );

  const ownerCandidate = artwork?.owner ?? artwork?.product_owner ?? artwork?.owner_profile ?? null;
  let ownerId = null;
  if (typeof ownerCandidate === "string" || typeof ownerCandidate === "number") {
    ownerId = `${ownerCandidate}`;
  } else if (ownerCandidate && typeof ownerCandidate === "object") {
    ownerId = ownerCandidate.id ?? ownerCandidate._id ?? ownerCandidate.owner_id ?? ownerCandidate.ownerId ?? null;
    ownerId = ownerId ? `${ownerId}` : null;
  }

  const holdingType = artwork?.metadata?.holding_type ?? artwork?.product_metadata?.holding_type ?? artwork?.holding_type ?? null;

  return {
    holdingId: holdingId ? `${holdingId}` : null,
    ownerId,
    holdingType: holdingType ? `${holdingType}` : null,
  };
}

export function matchesArtworkRouteContext(artwork, options = {}) {
  const targetSlug = options.slug ?? null;
  const targetHoldingId = options.holdingId ?? null;
  const targetOwnerId = options.ownerId ?? null;
  const targetHoldingType = options.holdingType ?? null;

  if (targetSlug && resolveArtworkSlug(artwork, artwork?.product_id ?? artwork?._id) !== targetSlug) {
    return false;
  }

  if (!targetHoldingId) {
    return true;
  }

  const identity = resolveArtworkHoldingIdentifiers(artwork);
  if (identity.holdingId !== `${targetHoldingId}`) {
    return false;
  }

  if (targetOwnerId && identity.ownerId && identity.ownerId !== `${targetOwnerId}`) {
    return false;
  }

  if (targetHoldingType && identity.holdingType && identity.holdingType !== `${targetHoldingType}`) {
    return false;
  }

  return true;
}

export function isLikelyMongoObjectId(value) {
  return typeof value === "string" && /^[a-fA-F0-9]{24}$/.test(value);
}

export function appendOffsetToEndpoint(endpoint, offset) {
  if (!endpoint || offset === undefined || offset === null) {
    return endpoint;
  }
  const hasQuery = endpoint.includes("?");
  const needsJoiner = hasQuery && !endpoint.endsWith("?") && !endpoint.endsWith("&");
  const joiner = hasQuery ? (needsJoiner ? "&" : "") : "?";
  return `${endpoint}${joiner}offset=${offset}`;
}

export function extractArtworkListFromResponse(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (payload && Array.isArray(payload.data)) {
    return payload.data;
  }
  if (payload?.data && Array.isArray(payload.data.data)) {
    return payload.data.data;
  }
  return [];
}

export function mergeUniqueArtworks(existingArtworks, incomingArtworks) {
  const safeExisting = Array.isArray(existingArtworks) ? existingArtworks : [];
  const safeIncoming = Array.isArray(incomingArtworks) ? incomingArtworks : [];
  const seen = new Set();
  const merged = [];

  const pushUnique = (item, fallbackPrefix, index) => {
    if (!item || typeof item !== "object") {
      return;
    }
    const fallback = `${fallbackPrefix}_${index}`;
    const key = resolveArtworkSlug(item, item?._id ?? item?.product_id ?? fallback) ?? fallback;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    merged.push(item);
  };

  safeExisting.forEach((item, index) => pushUnique(item, "existing", index));
  safeIncoming.forEach((item, index) => pushUnique(item, "incoming", index));

  return merged;
}

export function cloneArtworksPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return payload;
  }

  const clone = { ...payload };

  if (Array.isArray(payload.data)) {
    clone.data = payload.data.map((item) => {
      if (item && typeof item === "object") {
        return { ...item };
      }
      return item;
    });
    return clone;
  }

  if (payload.data && typeof payload.data === "object" && Array.isArray(payload.data.data)) {
    clone.data = {
      ...payload.data,
      data: payload.data.data.map((item) => {
        if (item && typeof item === "object") {
          return { ...item };
        }
        return item;
      }),
    };
    return clone;
  }

  return clone;
}

export function sanitizeArtworkFeedPayload(payload) {
  const clonedPayload = cloneArtworksPayload(payload);
  if (!clonedPayload || !Array.isArray(clonedPayload.data)) {
    return clonedPayload;
  }

  const filteredData = clonedPayload.data.filter((item) => {
    if (!item || typeof item !== "object") {
      return false;
    }
    if (!resolveArtworkSlug(item, item?._id ?? item?.product_id ?? null)) {
      return false;
    }
    return !isRestrictedArtwork(item);
  });

  if (filteredData.length === clonedPayload.data.length) {
    return clonedPayload;
  }

  return {
    ...clonedPayload,
    data: filteredData,
    totalDocs: Math.min(
      Number.isFinite(clonedPayload.totalDocs) ? clonedPayload.totalDocs : filteredData.length,
      filteredData.length,
    ),
  };
}

export function markArtworkForViewer(artwork, viewerSessionId) {
  if (!artwork || typeof artwork !== "object") {
    return artwork;
  }

  return {
    ...artwork,
    _viewerStateHydratedFor: viewerSessionId ?? null,
  };
}
